import { itineraryApi, useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import { TRIPADD } from '@/src/constants/itinerary/insurance'
import { getItineraryValidationErrors, setInsuranceTermsAndConditionsError } from '@/src/redux/slices/itinerarySlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'

import { VasAncillary } from '@/src/types/itinerary'
import { useCallback, useMemo } from 'react'
import useClevertapForItinerary from '../../analytics/clevertap/itinerary'
import useGetItineraryId from '../useGetItineraryId'

const useInsurance = () => {
    const itineraryId = useGetItineraryId()
    const dispatch = useAppDispatch()

    const {
        insurance: { termsAndConditions: insuranceTermsAndConditionsError }
    } = useAppSelector(getItineraryValidationErrors)

    const { insuranceList, selectedInsurances, isInsuranceTermsAndConditionsChecked } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => {
                const {
                    insuranceList = [],
                    selectedInsurances = [],
                    isInsuranceTermsAndConditionsChecked = false
                } = data?.insurance || {}
                return {
                    insuranceList,
                    selectedInsurances,
                    isInsuranceTermsAndConditionsChecked
                }
            }
        }
    )

    const { triggerItineraryEvent } = useClevertapForItinerary()

    const handleInsuranceClick = useCallback((insurance: VasAncillary, actionType: 'ADD' | 'REMOVE') => {
        dispatch(
            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                const { selectedInsurances = [] } = draft?.insurance || {}
                if (selectedInsurances) {
                    if (actionType === 'ADD') {
                        triggerItineraryEvent('Air_UI_Action', {
                            action_value: insurance.vasType,
                            action_name: 'insurance_added',
                            action_type: 'TRIPADD'
                        })
                        draft.insurance.isInsuranceTermsAndConditionsChecked = true
                        draft.insurance.selectedInsurances.push(insurance)
                    } else {
                        triggerItineraryEvent('Air_UI_Action', {
                            action_value: insurance.vasType,
                            action_name: 'insurance_removed',
                            action_type: 'TRIPADD'
                        })
                        const filteredInsurances = selectedInsurances.filter(item => item.vasType !== insurance.vasType)
                        draft.insurance.selectedInsurances = filteredInsurances
                        if (filteredInsurances.length === 0) {
                            draft.insurance.isInsuranceTermsAndConditionsChecked = false
                        }
                    }
                }
            })
        )
    }, [])

    const termsAndConditionsUrl = useMemo(() => {
        const tripAddDataArr = insuranceList?.filter(insurnace => insurnace.vasProvider === TRIPADD)
        const { vasDetails = [] } = tripAddDataArr?.[0] || {}
        return vasDetails?.[0]?.termsAndConditions?.url
    }, [insuranceList])

    const onTermsChange = useCallback(() => {
        dispatch(setInsuranceTermsAndConditionsError(false))
        dispatch(
            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                draft.insurance.isInsuranceTermsAndConditionsChecked =
                    !draft.insurance.isInsuranceTermsAndConditionsChecked
            })
        )
    }, [])

    return {
        insuranceList,
        isInsuranceTermsAndConditionsChecked,
        selectedInsurances,
        termsAndConditionsUrl,
        insuranceTermsAndConditionsError,
        handleInsuranceClick,
        onTermsChange
    }
}

export default useInsurance
